/* Building a policy from rules. **No Rego, and none needed.**
 *
 * A row is a role, an action, a resource type, optionally one resource, and allow or deny. The
 * platform validates every reference against its own vocabulary, generates the module, compiles it
 * and evaluates it once before storing — so OPA never appears in this component except in the
 * read-only advanced view at the bottom, which nothing in the workflow requires.
 *
 * **Every dropdown is populated from `/v1/policy/vocabulary` rather than a list in this file.** A
 * rule naming a role, an action or a resource type that does not exist compiles, loads and matches
 * nothing — measured — so a hardcoded copy here would keep rendering while offering choices the
 * platform no longer governs, or omitting a role the tenant created yesterday.
 *
 * The panel below the rules is the part a list of rows cannot supply: deny wins, so a broad allow
 * read literally overstates what it grants, and a specific allow under a broad deny grants nothing
 * while looking like it grants something. `decisionsFor` says which.
 */

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAuthorFromRules,
  useEnforcement,
  usePolicyVocabulary,
  useValidatePolicy,
  useVersionPolicy,
} from '../api/policy';
import {
  actionsFor,
  blankRule,
  blockedReasonKey,
  builtPolicyWouldDecide,
  canSubmit,
  decisionsFor,
  documentFrom,
  draftFrom,
  isComplete,
  permitsNothing,
  policyLevelProblems,
  problemsForRule,
  saveReading,
  validationReading,
  withEdit,
} from '../model/builder';
import type { DraftRule } from '../model/builder';
import { useDatasetNames } from '@/features/catalog-browser';
import type { PolicyBundle, PolicyProblem } from '@/shared/api/generated/policy';
import { Alert } from '@/shared/ui/Alert';
import { SkeletonRows } from '@/shared/ui/Skeleton';

/** The one resource type this build can offer *specific* resources for.
 *
 * Every governed type can be named broadly — "every rule", "every connection" — because that needs
 * no id. Naming one instance needs a list of instances to choose from, and the catalogue is the only
 * such list this screen has. Offering a free-text UUID box for the other thirty-six would be a
 * field where a typo produces a rule that matches nothing, silently, which is the failure this whole
 * feature is arranged against.
 */
const PICKABLE_RESOURCE = 'catalog.dataset';

export interface PolicyBuilderProps {
  readonly bundle: PolicyBundle;
  /** The active version, so the builder reopens what is running rather than a blank form. */
  readonly activeSemver: string | null;
}

export function PolicyBuilder({
  bundle,
  activeSemver,
}: PolicyBuilderProps): ReactElement {
  const { t } = useTranslation();
  const vocabulary = usePolicyVocabulary();
  const enforcement = useEnforcement();
  const saved = useVersionPolicy(bundle.id, activeSemver);
  const datasets = useDatasetNames();
  const validate = useValidatePolicy();
  const author = useAuthorFromRules();

  const [rules, setRules] = useState<readonly DraftRule[] | null>(null);
  const [showGenerated, setShowGenerated] = useState(false);

  /* Seeded once from whichever answer arrives — the running version's rules, or one blank row — and
   * never again. Re-seeding on every render of a settled query would discard what somebody is part
   * way through editing the moment an unrelated refetch lands.
   *
   * **Nothing is seeded until the saved-rules read has settled, and that is a bug this caught.**
   * The first version ran on every render: the read was still pending, so `seed` was null, the
   * blank row won, and the effect then declined to replace it — so a steward opening a live policy
   * saw an *empty form* and would reasonably conclude their rules were gone. Waiting for the read
   * is what makes "reopen what is running" true rather than merely intended. */
  const settled = activeSemver === null || saved.isSuccess || saved.isError;
  const seed =
    saved.isSuccess && saved.data.rules !== null ? draftFrom(saved.data.rules) : null;
  useEffect(() => {
    if (!settled) return;
    setRules((current) => current ?? seed ?? [blankRule()]);
  }, [settled, seed]);

  if (vocabulary.isPending) return <SkeletonRows rows={3} columns={5} />;
  if (vocabulary.isError) {
    return (
      <Alert tone="error" title={t('policy.build.unreadable.title')}>
        {t('policy.build.unreadable.body')}
      </Alert>
    );
  }

  const vocab = vocabulary.data;
  const rows = rules ?? [];
  const busy = validate.isPending || author.isPending;
  const checked = validationReading(validate.data, validate.isPending, validate.isError);
  const stored = saveReading(author.data, author.isPending, author.isError);

  /* The save's problems win when there are any: they are about the policy somebody just tried to
   * store, where a validation result may be several edits old. */
  const problems: readonly PolicyProblem[] =
    stored.problems.length > 0
      ? stored.problems
      : validate.data?.valid === false
        ? validate.data.problems
        : [];

  const blocked = blockedReasonKey(rows, vocab);
  const decisions = decisionsFor(rows);

  function edit(
    index: number,
    field: Parameters<typeof withEdit>[1],
    value: string,
  ): void {
    setRules((current) =>
      (current ?? []).map((rule, at) =>
        at === index ? withEdit(rule, field, value) : rule,
      ),
    );
    /* Both results are cleared on an edit. "Valid" or "Saved" left standing beside changed rules is
     * a statement about a policy that no longer exists. */
    validate.reset();
    author.reset();
  }

  return (
    <>
      {vocab.roles.length === 0 && (
        <Alert tone="warning" title={t('policy.build.noRoles.title')}>
          {t('policy.build.noRoles.body')}
        </Alert>
      )}
      {!vocab.compiler_available && (
        <Alert tone="notice" title={t('policy.build.noCompiler.title')}>
          {t('policy.build.noCompiler.body')}
        </Alert>
      )}
      {/* Said before the work rather than after it: every step below succeeds on a deployment whose
          evaluator is unwired, and the policy then decides nothing. */}
      {enforcement.isSuccess && !builtPolicyWouldDecide(enforcement.data) && (
        <Alert tone="warning" title={t('policy.build.inert.title')}>
          {t('policy.build.inert.body')}
        </Alert>
      )}

      <p className="lq-note">{t('policy.build.intro', { bundle: bundle.name })}</p>

      <div className="lq-pb">
        {rows.map((rule, index) => {
          const rowProblems = problemsForRule(problems, index);
          const actions = actionsFor(vocab, rule.resourceType);
          return (
            <div
              className={`lq-pb__rule${rowProblems.length > 0 ? ' lq-pb__rule--bad' : ''}`}
              key={rule.id}
              role="group"
              aria-label={t('policy.build.rule', { number: index + 1 })}
            >
              {/* **One header row, rather than a legend above and a Remove row below.** The rule
                  was a `<fieldset>`, and `<legend>` gave nowhere to put the row's own control -- so
                  Remove sat right-aligned on a line of its own and every rule spent a third of its
                  height on one word. `role="group"` with a label is exactly what fieldset/legend
                  supplied semantically, and it leaves the header free to hold both. */}
              <div className="lq-pb__head">
                <span className="lq-pb__legend">
                  {t('policy.build.rule', { number: index + 1 })}
                  {!isComplete(rule) && (
                    <span className="lq-pb__incomplete">
                      {t('policy.build.incomplete')}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="lq-rowaction"
                  disabled={busy || rows.length === 1}
                  /* The last row cannot be removed: an empty policy would refuse every request the
                     roles permit, which the server refuses too. Disabled with the reason in the
                     title rather than hidden, so the control does not appear and disappear. */
                  title={
                    rows.length === 1
                      ? t('policy.build.cannotRemoveLast')
                      : t('policy.build.remove')
                  }
                  onClick={() => {
                    setRules((current) =>
                      (current ?? []).filter((_each, at) => at !== index),
                    );
                    validate.reset();
                    author.reset();
                  }}
                >
                  {t('policy.build.remove')}
                </button>
              </div>

              <div className="lq-pb__fields">
                <Picker
                  label={t('policy.build.field.role')}
                  value={rule.role}
                  options={vocab.roles.map((role) => ({ value: role, label: role }))}
                  placeholder={t('policy.build.choose')}
                  onPick={(value) => {
                    edit(index, 'role', value);
                  }}
                />
                <Picker
                  label={t('policy.build.field.resource')}
                  value={rule.resourceType}
                  options={vocab.resources.map((each) => ({
                    value: each.resource_type,
                    label: each.resource_type,
                  }))}
                  placeholder={t('policy.build.choose')}
                  onPick={(value) => {
                    edit(index, 'resourceType', value);
                  }}
                />
                <Picker
                  label={t('policy.build.field.action')}
                  value={rule.action}
                  options={actions.map((action) => ({ value: action, label: action }))}
                  placeholder={
                    rule.resourceType === null
                      ? t('policy.build.pickResourceFirst')
                      : t('policy.build.choose')
                  }
                  disabled={rule.resourceType === null}
                  onPick={(value) => {
                    edit(index, 'action', value);
                  }}
                />
                <Picker
                  label={t('policy.build.field.which')}
                  value={rule.resourceId}
                  /* "Every" first and always available: it needs no id, and it is how a broad rule
                     is written. A specific resource is offered only for the one type this screen
                     has a list of — see `PICKABLE_RESOURCE`. */
                  options={
                    rule.resourceType === PICKABLE_RESOURCE && datasets.isSuccess
                      ? [...datasets.data.byId].map(([id, name]) => ({
                          value: id,
                          label: name,
                        }))
                      : []
                  }
                  placeholder={t('policy.build.everyResource', {
                    resource: rule.resourceType ?? '',
                  })}
                  disabled={rule.resourceType === null}
                  onPick={(value) => {
                    edit(index, 'resourceId', value);
                  }}
                />
                <Picker
                  label={t('policy.build.field.decision')}
                  value={rule.effect}
                  options={vocab.effects.map((effect) => ({
                    value: effect,
                    label: t(`policy.build.effect.${effect}`),
                  }))}
                  placeholder={t('policy.build.choose')}
                  onPick={(value) => {
                    edit(index, 'effect', value);
                  }}
                />
              </div>

              {rowProblems.length > 0 && (
                <ul className="lq-pb__problems">
                  {rowProblems.map((problem) => (
                    <li
                      className="lq-pb__problem"
                      key={`${problem.code}-${problem.message}`}
                    >
                      {problem.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Above the footer rather than in it, because it belongs to the rules: adding one is
          editing, and the two controls below the rule commit. */}
      <button
        type="button"
        className="lq-pb__addRule"
        disabled={busy || rows.length >= vocab.max_rules}
        title={
          rows.length >= vocab.max_rules
            ? t('policy.build.atCeiling', { max: vocab.max_rules })
            : t('policy.build.add')
        }
        onClick={() => {
          setRules((current) => [...(current ?? []), blankRule()]);
          validate.reset();
          author.reset();
        }}
      >
        {t('policy.build.add')}
      </button>

      <div className="lq-pb__actions">
        <button
          type="button"
          className="lq-btn lq-btn--secondary"
          disabled={busy || !rows.every(isComplete)}
          title={checked.reading === 'valid' ? t('policy.build.valid.body') : undefined}
          onClick={() => {
            validate.mutate(documentFrom(rows));
          }}
        >
          {t('policy.build.validate')}
          {checked.reading === 'valid' && (
            /* **A dot rather than a panel.** A sound check is the expected outcome, and four lines
               of prose asserting it pushed the decision summary -- the part that says what the
               rules actually grant -- off the screen. `aria-hidden` keeps the button's accessible
               name "Validate policy", so the mark is decoration; the sentence it replaced is
               announced once by the live region below and is on the button's own tooltip. */
            <span className="lq-pb__passed" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          className="lq-btn lq-btn--primary"
          disabled={!canSubmit(rows, vocab, busy)}
          onClick={() => {
            author.mutate({ bundleId: bundle.id, policy: documentFrom(rows) });
          }}
        >
          {t('policy.build.save')}
        </button>
      </div>

      {/* **Below the buttons rather than trailing them.** Inline, the reason ran on from the
          disabled control as though it were part of the label -- "Save as draft This deployment
          cannot build policies" -- and the one sentence a blocked author needs to read was the one
          shaped like an afterthought. */}
      {blocked !== null && <p className="lq-pb__blocked">{t(blocked)}</p>}

      {checked.reading === 'checking' && (
        <p className="lq-muted">{t('policy.build.checking')}</p>
      )}
      {checked.reading === 'failed' && (
        <Alert tone="error" title={t('policy.build.checkFailed')}>
          {validate.error?.message ?? ''}
        </Alert>
      )}
      {/* Announced without occupying the page. Removing the panel must not remove the fact from
          a screen reader, which a bare coloured dot would -- so the sentence lives here, visually
          hidden, in a live region that speaks it once when the verdict arrives. */}
      <p className="lq-visually-hidden" role="status">
        {checked.reading === 'valid' ? t('policy.build.valid.title') : ''}
      </p>
      {stored.reading === 'stored' && (
        <Alert tone="ok" title={t('policy.build.stored.title')}>
          {t('policy.build.stored.body')}
        </Alert>
      )}
      {stored.reading === 'failed' && (
        <Alert tone="error" title={t('policy.build.saveFailed')}>
          {author.error?.message ?? ''}
        </Alert>
      )}
      {stored.reading === 'refused' && (
        <Alert tone="warning" title={t('policy.build.refused.title')}>
          {t('policy.build.refused.body')}
        </Alert>
      )}

      {policyLevelProblems(problems).length > 0 && (
        <ul className="lq-pb__problems">
          {policyLevelProblems(problems).map((problem) => (
            <li className="lq-pb__problem" key={`${problem.code}-${problem.message}`}>
              {problem.message}
            </li>
          ))}
        </ul>
      )}

      {/* **A policy of nothing but denies refuses everything**, which no row says and the
          deny-overrides note below states only in general. Here rather than at the top of the
          panel, because it is a finding about these rules rather than about the deployment, and
          this is where the screen answers what they decide. */}
      {permitsNothing(rows) && (
        <Alert tone="warning" title={t('policy.build.denyOnly.title')}>
          {t('policy.build.denyOnly.body')}
        </Alert>
      )}

      {/* **What the rules actually decide**, which the rows above do not state on their own. */}
      {decisions.length > 0 && (
        <div className="lq-pb__summary">
          <h4 className="lq-ap__panelHead">{t('policy.build.summary.title')}</h4>
          <ul className="lq-pb__decisions">
            {decisions.map((decision) => (
              <li className="lq-pb__decision" key={decision.id}>
                <span
                  className={`lq-pb__effect lq-pb__effect--${decision.effect}`}
                  aria-label={t(`policy.build.effect.${decision.effect}`)}
                >
                  {t(`policy.build.effect.${decision.effect}`)}
                </span>
                <span className="lq-pb__sentence">
                  {t('policy.build.summary.line', {
                    role: decision.role,
                    action: decision.action,
                    what:
                      decision.resourceId === null
                        ? t('policy.build.summary.every', {
                            resource: decision.resourceType,
                          })
                        : (datasets.data?.byId.get(decision.resourceId) ??
                          decision.resourceId),
                  })}
                </span>
                {decision.note === 'narrowed' && (
                  <span className="lq-pb__note">
                    {t('policy.build.summary.narrowed', {
                      rules: decision.by.join(', '),
                    })}
                  </span>
                )}
                {decision.note === 'shadowed' && (
                  <span className="lq-pb__note lq-pb__note--bad">
                    {t('policy.build.summary.shadowed', {
                      rules: decision.by.join(', '),
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="lq-ap__panelNote">{t('policy.build.summary.denyWins')}</p>
        </div>
      )}

      {/* **Read-only, collapsed, and not part of the workflow.** Here for somebody diagnosing an
          unexpected decision; the rules above are how a policy is written. */}
      {saved.isSuccess && saved.data.rego !== null && (
        <details
          className="lq-pb__generated"
          open={showGenerated}
          onToggle={(event) => {
            setShowGenerated(event.currentTarget.open);
          }}
        >
          <summary className="lq-pb__generatedHead">
            {t('policy.build.generated.title')}
          </summary>
          <p className="lq-ap__panelNote">{t('policy.build.generated.note')}</p>
          <pre className="lq-pb__rego">{saved.data.rego}</pre>
        </details>
      )}
    </>
  );
}

interface Option {
  readonly value: string;
  readonly label: string;
}

/** One labelled dropdown.
 *
 * A `<select>` rather than a combobox: the lists are short and closed, and a native select is
 * keyboard-accessible and screen-reader-correct without any of the work a custom one needs. The
 * empty option carries the placeholder, so an unchosen field reads as a question rather than as the
 * first alphabetical answer — which is what a select with no empty option silently asserts.
 */
function Picker({
  label,
  value,
  options,
  placeholder,
  disabled = false,
  onPick,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly options: readonly Option[];
  readonly placeholder: string;
  readonly disabled?: boolean;
  readonly onPick: (value: string) => void;
}): ReactElement {
  return (
    <label className="lq-pb__field">
      <span className="lq-pb__label">{label}</span>
      <select
        className="lq-pb__select"
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => {
          onPick(event.target.value);
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
